import React from "react";
import { motion } from "framer-motion";

// Import illustration images
import webdImg from "../images/webd.png";
import digitalImg from "../images/digital.jpg";
import consultancyImg from "../images/consultancy.jpg";
import cyberImg from "../images/cyber.jpg";
import softwareImg from "../images/software.jpg";
import androidImg from "../images/android.jpg";
import hostingImg from "../images/web3.png";
import vaptImg from "../images/cyber.jpg";

const serviceData = [
  {
    id: 1,
    img: webdImg,
    title: "Website Design and Development",
    subtitle: "Frontend & Backend Solutions",
    desc: "We develop smart, secure, and scalable web applications for multiple digital platforms. We allow for an exhaustive and seamless experience, matching custom enterprise needs.",
    subservices: [
      {
        title: "Web Development",
        desc: "We develop secure, mobile responsive, and interactive websites for our customers which range from rapid deployment to robust platform development.",
      },
      {
        title: "Native App Development",
        desc: "We develop and deploy native applications for iOS and Android platforms to address your security requirements.",
      },
      {
        title: "Hybrid App Development",
        desc: "Our team of experts with exposure to multiple platforms design and deliver secure, high-performing solution architecture.",
      },
    ],
  },
  {
    id: 2,
    img: digitalImg,
    title: "Digital Marketing and SEO",
    subtitle: "Data-Driven Strategies",
    desc: "Strategic campaigns that increase your online visibility, engage your target audience, and convert visitors into loyal customers.",
    subservices: [
      {
        title: "Search Engine Optimization (SEO)",
        desc: "Empower your brand visibility in search engines with organic keyword integration and technical audits.",
      },
      {
        title: "Social Media Marketing",
        desc: "Increase your brand value across popular platforms with targeted content delivery and community management.",
      },
      {
        title: "Pay-Per-Click Advertising",
        desc: "Drive high-intent traffic with conversion-optimized campaigns on Google Ads and Meta Ads.",
      },
    ],
  },
  {
    id: 3,
    img: consultancyImg,
    title: "Business Consultancy",
    subtitle: "Expert IT Guidance",
    desc: "Strategic advisory services to help you navigate the digital landscape, optimize operations, and accelerate business growth.",
    subservices: [
      {
        title: "Market Research",
        desc: "Gain valuable insights into your target market, customer behavior, and industry trends to make informed decisions.",
      },
      {
        title: "IT Infrastructure Design",
        desc: "Plan, optimize, and streamline corporate tech stacks for speed, security, and scalability.",
      },
      {
        title: "Operational Auditing",
        desc: "Analyze workflows and operational procedures to boost overall productivity and revenue growth.",
      },
    ],
  },
  {
    id: 4,
    img: cyberImg,
    title: "Cyber Security Training",
    subtitle: "Advanced Protection",
    desc: "Comprehensive security solutions that safeguard your digital assets, protect sensitive data, and prevent cyber threats.",
    subservices: [
      {
        title: "Corporate Workshops",
        desc: "Train your team on secure cyber hygiene, phishing prevention, and credential management.",
      },
      {
        title: "Secure Coding Standards",
        desc: "Equip software development teams with the skills to construct exploit-free code frameworks.",
      },
      {
        title: "Risk Assessment Protocols",
        desc: "Identify vulnerabilities in existing software platforms and outline robust remediation plans.",
      },
    ],
  },
  {
    id: 5,
    img: softwareImg,
    title: "Custom Software Development",
    subtitle: "Custom Enterprise Solutions",
    desc: "Tailored software solutions designed to address your unique business challenges and drive operational efficiency.",
    subservices: [
      {
        title: "ERP & CRM Systems",
        desc: "Build tailored systems to coordinate customer relationships and internal organizational resources.",
      },
      {
        title: "API Development & Integration",
        desc: "Connect diverse software stacks through highly secure and lightning-fast REST/GraphQL APIs.",
      },
      {
        title: "Legacy System Migration",
        desc: "Safely upgrade obsolete systems to modern cloud architectures without risk of data loss.",
      },
    ],
  },
  {
    id: 6,
    img: androidImg,
    title: "Mobile App Development",
    subtitle: "iOS & Android Apps",
    desc: "Intuitive, feature-rich mobile applications that deliver exceptional user experiences across all devices and platforms.",
    subservices: [
      {
        title: "Cross-Platform Mobile Apps",
        desc: "Deliver high-fidelity apps on iOS and Android simultaneously using React Native and Flutter.",
      },
      {
        title: "UX/UI Design for Mobile",
        desc: "Design interfaces optimized for thumb reach, responsiveness, and dark-mode elegance.",
      },
      {
        title: "App Store Optimization (ASO)",
        desc: "Deploy your app on Apple App Store & Google Play with targeted discoverability optimization.",
      },
    ],
  },
  {
    id: 7,
    img: hostingImg,
    title: "Hosting Services",
    subtitle: "Cpanel Dynamic Hosting",
    desc: "High-performance, secure, and reliable hosting plans built to keep your sites running smoothly 24/7.",
    subservices: [
      {
        title: "Shared & Dedicated Servers",
        desc: "Choose optimal server capacities styled to match scaling online user traffic.",
      },
      {
        title: "cPanel Panel Management",
        desc: "Effortlessly manage directories, emails, databases, and SSL certificates.",
      },
      {
        title: "Daily Backups & Uptime Guarantee",
        desc: "Sleep soundly with secure off-site daily backups and 99.9% network uptime.",
      },
    ],
  },
  {
    id: 8,
    img: vaptImg,
    title: "VAPT Audit Services",
    subtitle: "Vulnerability Assessment & Pentesting",
    desc: "Determine potential vulnerabilities in your systems and network architectures before malicious actors can exploit them.",
    subservices: [
      {
        title: "Vulnerability Scanning",
        desc: "Run automated assessments across systems to identify unpatched software or weak configurations.",
      },
      {
        title: "Penetration Testing",
        desc: "Conduct authorized mock cyberattacks on your applications to check strength under pressure.",
      },
      {
        title: "Compliance Audits",
        desc: "Align corporate security procedures with regulatory standards like ISO 27001 and PCI DSS.",
      },
    ],
  },
];

const ServiceSlider = () => {
  return (
    <section className="services-list-container py-4 position-relative">
      <div className="container position-relative" style={{ zIndex: 2 }}>
        <div className="d-flex flex-column gap-5">
          {serviceData.map((service, index) => {
            const isEven = index % 2 === 1;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-120px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={`glass-service-row ${isEven ? "row-even" : ""}`}
              >
                {/* Left Side: Water droplet morphed Image Container */}
                <div className="glass-image-column">
                  <div className="glass-image-wrapper">
                    <div className="water-droplet-frame">
                      <img
                        src={service.img}
                        alt={service.title}
                        className="service-illustration"
                      />
                    </div>
                  </div>
                </div>

                {/* Center pointer Chevron */}
                <div className="chevron-divider-column">
                  <div className="chevron-divider-flow">
                    <i className="bi bi-chevron-right arrow-1"></i>
                    <i className="bi bi-chevron-right arrow-2"></i>
                    <i className="bi bi-chevron-right arrow-3"></i>
                  </div>
                </div>

                {/* Right Side: Details & Subservices list */}
                <div className="service-content-column">
                  <div className="service-content-card">
                    <span className="service-row-subtitle">{service.subtitle}</span>
                    <h3 className="service-row-title">{service.title}</h3>
                    <p className="service-row-desc">{service.desc}</p>

                    <ul className="subservices-list">
                      {service.subservices.map((sub, subIdx) => (
                        <li key={subIdx} className="subservice-item">
                          <span className="subservice-bullet">
                            <i className="bi bi-check2"></i>
                          </span>
                          <div className="subservice-info">
                            <h4 className="subservice-title">{sub.title}</h4>
                            <p className="subservice-desc">{sub.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServiceSlider;
